import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import {
  Ban,
  Bell,
  Bot,
  Building2,
  ChartNoAxesCombined,
  Check,
  CookingPot,
  CreditCard,
  Globe,
  Heart,
  LayoutGrid,
  MonitorSmartphone,
  Package,
  Plug,
  Printer,
  QrCode,
  Receipt,
  ScanLine,
  ShieldCheck,
  Smartphone,
  Tv,
  User,
  Users,
  type LucideIcon,
} from "lucide-react";
import {
  COMPONENTS,
  INCLUDED_KEYS,
  formatMoney,
  toMoney,
  type ComponentKey,
  type IncludedKey,
} from "@/data/pricing";
import { MARKET } from "@/market";
import { useModal } from "@/context/ModalContext";
import { Badge } from "../ui/badge";


const paidIcons: Record<ComponentKey, LucideIcon> = {
  branches: Building2,
  employees: Users,
  pos_devices: Smartphone,
  online_orders: Globe,
  waiter_app: User,
  qr_menu: QrCode,
  yandex: ScanLine,
  monitors: Tv,
  warehouses: Package,
};

const includedIcons: Record<IncludedKey, LucideIcon> = {
  kds: CookingPot,
  manager_app: Users,
  loyalty_crm: Heart,
  ai_analytics: Bot,
  kiosk: MonitorSmartphone,
  open_api: Plug,
  stop_list: Ban,
  cash_shift: Receipt,
  order_management: LayoutGrid,
  basic_analytics: ChartNoAxesCombined,
  order_status: Bell,
  e_kassa: ScanLine,
  pin_protection: ShieldCheck,
  floor_plan: LayoutGrid,
  owner_push: Bell,
  qr_generator: QrCode,
  payment_types: CreditCard,
  receipt_printer: Printer,
};

export function Modules() {
  const { t, i18n } = useTranslation();
  const { open } = useModal();

  const brvFmt = (value: number) => value.toLocaleString(i18n.language, { maximumFractionDigits: 3 });

  return (
    <section id="modules" className="bg-gradient-to-b from-slate-50 to-white py-[96px] overflow-hidden">
      <div className="mx-auto max-w-[1200px] px-4 sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center"
        >
          <h2 className="text-[32px] sm:text-[48px] leading-[1.15] font-bold tracking-[-0.02em] text-foreground">
            {t("components.title")}
          </h2>
          <p className="mt-4 text-foreground-muted text-[17px] sm:text-[18px] max-w-[640px] mx-auto leading-relaxed">
            {t("components.subtitle")}
          </p>
        </motion.div>

        {/* ── Priced components ── */}
        <div className="mt-16">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-1 h-6 rounded-full bg-primary" />
            <p className="text-[12px] font-bold tracking-[0.1em] uppercase text-primary">
              {t("components.paid_title")}
            </p>
            <div className="flex-1 h-px bg-gradient-to-r from-primary/25 to-transparent ml-3" />
          </div>
          <p className="mb-6 text-[13px] text-foreground/40 leading-relaxed max-w-[680px]">
            {t("components.paid_note")}
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {COMPONENTS.map((component, idx) => {
              const Icon = paidIcons[component.key];
              return (
                <motion.div
                  key={component.key}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: idx * 0.05 }}
                  whileHover={{ y: -4 }}
                  className="group relative flex flex-col rounded-xl border border-foreground/[0.08] bg-white p-5 transition-all duration-300 hover:border-primary/30 hover:shadow-xl"
                >
                  <div className="absolute inset-x-0 top-0 h-0.5 origin-left scale-x-0 rounded-t-xl bg-primary transition-transform duration-300 group-hover:scale-x-100" />

                  <div className="flex items-start gap-4">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary/[0.08] text-primary transition-shadow group-hover:shadow-md">
                      <Icon className="h-5 w-5" strokeWidth={1.8} />
                    </div>

                    <div className="min-w-0 flex-1">
                      <h3 className="text-[15px] font-bold leading-tight text-foreground">
                        {t(`components.items.${component.key}.name`)}
                      </h3>
                      <p className="mt-1 text-[12.5px] leading-relaxed text-foreground/45">
                        {t(`components.items.${component.key}.desc`)}
                      </p>
                    </div>
                  </div>

                  <div className="mt-4 flex flex-wrap items-baseline gap-x-2 gap-y-1 border-t border-foreground/[0.07] pt-3.5">
                    <span className="text-[19px] font-bold tabular-nums text-foreground">
                      {formatMoney(toMoney(component.brv))}
                    </span>
                    <span className="text-[12px] font-medium text-foreground/40">
                      {t("components.per_month")}
                    </span>
                    {MARKET.pricing.showUnit && (
                      <span className="ml-auto rounded-md bg-foreground/[0.05] px-2 py-0.5 text-[11px] font-medium tabular-nums text-foreground/50">
                        {brvFmt(component.brv)} {t("pricing.brv_unit")}
                      </span>
                    )}
                  </div>

                  {(component.perBranch || component.min) && (
                    <p className="mt-2 text-[11.5px] text-foreground/35">
                      {component.perBranch ? t("components.per_branch") : t("components.min_one")}
                    </p>
                  )}
                </motion.div>
              );
            })}
          </div>

          <div className="mt-8 flex justify-center">
            <a
              href="#pricing"
              className="inline-flex h-11 items-center gap-2 rounded-xl bg-primary px-6 text-[14px] font-semibold text-white shadow-sm transition-all hover:bg-primary-dark hover:shadow-md"
            >
              {t("components.cta")}
            </a>
          </div>
        </div>

        {/* ── Included at no extra cost ── */}
        <div className="mt-20">
          <div className="flex items-center gap-2 mb-6">
            <div className="w-1 h-6 rounded-full bg-emerald-500" />
            <p className="text-[12px] font-bold tracking-[0.1em] uppercase text-emerald-600">
              {t("components.free_title")}
            </p>
            <div className="flex-1 h-px bg-gradient-to-r from-emerald-200 to-transparent ml-3" />
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {INCLUDED_KEYS.map((key, idx) => {
              const Icon = includedIcons[key];
              return (
                <motion.div
                  key={key}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: Math.min(idx, 8) * 0.03 }}
                  whileHover={{ y: -2 }}
                  className="group relative overflow-visible rounded-xl border border-foreground/[0.08] bg-white p-4 transition-all duration-300 hover:border-emerald-200 hover:shadow-lg"
                >
                  {idx === 0 && (
                    <div className="absolute -right-2 -top-2 z-10">
                      <Badge className="h-auto gap-1 border-0 bg-emerald-50 px-2.5 py-1 text-[11px] font-bold text-emerald-700 shadow-md hover:bg-emerald-100">
                        <Check className="h-3 w-3" strokeWidth={3} />
                        {t("components.free_badge")}
                      </Badge>
                    </div>
                  )}

                  <div className="flex items-start gap-3">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-emerald-50 transition-colors group-hover:bg-emerald-100">
                      <Icon className="h-4 w-4 text-emerald-600" strokeWidth={1.8} />
                    </div>

                    <div className="min-w-0 flex-1">
                      <h3 className="pr-6 text-[14px] font-bold leading-tight text-foreground">
                        {t(`components.free_items.${key}.name`)}
                      </h3>
                      <p className="mt-1.5 text-[11.5px] leading-relaxed text-foreground/45">
                        {t(`components.free_items.${key}.desc`)}
                      </p>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>

          <div className="mt-8 flex justify-center">
            <button
              onClick={open}
              className="inline-flex h-11 items-center gap-2 rounded-xl border border-foreground/10 px-6 text-[14px] font-semibold text-foreground transition-all hover:border-primary/40 hover:text-primary"
            >
              {t("cta.cta_primary")}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
